// Intraday running-P&L series for a single trading day (calendar drill-in).
//
// The Calendar buckets P&L by UTC `closed_at` date; the Trades day-sessions
// endpoint (GET /trades/day-sessions) reconciles with it by the same close
// date. This module flattens a day's sessions into the realized closed trades,
// orders them by close time, and accumulates the running total so the final
// value equals that day's Calendar P&L. Open-position rows (null `closed_at`)
// and the pinned open-positions session contribute nothing.
//
// Inputs are typed structurally (not against the generated schema) so the
// builder stays a pure, trivially-testable function; the real
// DaySessionGroupResponse satisfies these shapes.

export interface IntradayTradeInput {
  closed_at: string | null;
  realized_pnl_amount?: string | null;
}

export interface IntradayGroupInput {
  trades: ReadonlyArray<IntradayTradeInput>;
}

export interface IntradaySessionInput {
  is_open_positions: boolean;
  currency: string;
  groups: ReadonlyArray<IntradayGroupInput>;
}

export interface IntradayCumulativePoint {
  // The trade's UTC close timestamp (ISO 8601), kept for the time-axis label.
  closedAt: string;
  // Epoch milliseconds of `closedAt` — the chart's x-position basis.
  closedAtMs: number;
  // This trade's own realized P&L.
  pnl: number;
  // Running realized total through (and including) this trade.
  cumulative: number;
}

// Flatten a day's sessions to the ordered realized running-P&L series.
export function buildIntradayCumulative(
  sessions: ReadonlyArray<IntradaySessionInput>,
): IntradayCumulativePoint[] {
  const closed: Array<{ closedAt: string; closedAtMs: number; pnl: number }> = [];
  for (const session of sessions) {
    if (session.is_open_positions) continue;
    for (const group of session.groups) {
      for (const trade of group.trades) {
        if (trade.closed_at === null || trade.closed_at === undefined) continue;
        if (trade.realized_pnl_amount === null || trade.realized_pnl_amount === undefined) {
          continue;
        }
        const pnl = Number(trade.realized_pnl_amount);
        const closedAtMs = new Date(trade.closed_at).getTime();
        if (!Number.isFinite(pnl) || Number.isNaN(closedAtMs)) continue;
        closed.push({ closedAt: trade.closed_at, closedAtMs, pnl });
      }
    }
  }
  closed.sort((a, b) => a.closedAtMs - b.closedAtMs);
  let running = 0;
  return closed.map((trade) => {
    running += trade.pnl;
    return { ...trade, cumulative: running };
  });
}

// Currency of the day's realized session (the first non-open session), so the
// chart's axis + tooltip match the rest of the day's figures. USD when the day
// carries no realized session.
export function intradayCurrency(
  sessions: ReadonlyArray<IntradaySessionInput>,
): string {
  for (const session of sessions) {
    if (!session.is_open_positions) return session.currency;
  }
  return "USD";
}
